import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, HardHat, CheckCircle2, Paperclip, X } from 'lucide-react';
import { publicExtrasRequestApi } from '../../lib/api';

interface RequestItem {
    type_label: string;
    quantity: number;
    start_time: string;
    end_time: string;
    desired_people: string[];
}

interface RequestInfo {
    partner_name: string;
    company_name: string;
    items: RequestItem[];
    request_date: string;
    confirmed_at: string | null;
}

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return d; }
};

const readFileAsDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Lecture du fichier impossible'));
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
});

export const ExtrasRequestConfirmPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [info, setInfo] = React.useState<RequestInfo | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [confirming, setConfirming] = React.useState(false);
    const [confirmedAt, setConfirmedAt] = React.useState<string | null>(null);
    const [file, setFile] = React.useState<File | null>(null);
    const [namesText, setNamesText] = React.useState('');

    React.useEffect(() => {
        if (!token) return;
        publicExtrasRequestApi.getInfo(token).then((d: any) => {
            setInfo(d);
            setConfirmedAt(d?.confirmed_at || null);
            const allDesired = Array.isArray(d?.items) ? d.items.flatMap((it: RequestItem) => it.desired_people || []) : [];
            if (allDesired.length > 0) setNamesText(allDesired.join('\n'));
        }).catch((e: any) => setError(e?.message || 'Lien invalide ou expiré.')).finally(() => setLoading(false));
    }, [token]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        // 2,8 Mo : au-delà, la base ne sait pas conserver le fichier (mesuré le 30/08).
        if (f.size > 2900000) { setError('Fichier trop volumineux (max ~2,8 Mo). Compressez-le puis réessayez.'); return; }
        setError('');
        setFile(f);
    };

    const handleConfirm = async () => {
        if (!token) return;
        setConfirming(true);
        setError('');
        try {
            let document: { file_name: string; mime_type: string; data: string } | undefined;
            if (file) {
                const dataUrl = await readFileAsDataUrl(file);
                document = { file_name: file.name, mime_type: file.type, data: dataUrl };
            }
            const names = namesText.split('\n').map((n) => n.trim()).filter(Boolean);
            const res: any = await publicExtrasRequestApi.confirm(token, document, names);
            setConfirmedAt(res?.confirmed_at || new Date().toISOString());
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la confirmation.');
        } finally {
            setConfirming(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-surface-app"><Loader2 size={32} className="animate-spin text-content-muted" /></div>;
    }

    if (error && !info) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4 text-center">
                <p className="text-content font-bold mb-1">Lien invalide</p>
                <p className="text-content-muted text-sm">{error}</p>
            </div>
        );
    }

    if (!info) return null;

    return (
        <div className="min-h-screen bg-surface-app py-10 px-4 flex items-center justify-center">
            <div className="max-w-md w-full bg-surface rounded-2xl border border-border p-8 space-y-6">
                <div className="text-center space-y-1">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-surface-soft border border-border flex items-center justify-center mb-3">
                        <HardHat size={26} className="text-content-muted" />
                    </div>
                    <h1 className="text-lg font-bold text-content">{info.partner_name}</h1>
                    <p className="text-sm text-content-muted">Demande de {info.company_name}</p>
                </div>

                <div className="space-y-2">
                    {info.request_date && (
                        <div className="flex items-center justify-between text-sm bg-surface-soft rounded-xl border border-border p-4">
                            <span className="text-content-muted">Date</span>
                            <span className="font-bold text-content">{fmtDate(info.request_date)}</span>
                        </div>
                    )}
                    {info.items.map((it, idx) => (
                        <div key={idx} className="bg-surface-soft rounded-xl border border-border p-4 space-y-2">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-content-muted">Type</span>
                                <span className="font-bold text-content">{it.type_label}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-content-muted">Quantité</span>
                                <span className="font-bold text-content">{it.quantity}</span>
                            </div>
                            {it.desired_people.length > 0 && (
                                <div className="flex items-start justify-between text-sm gap-2">
                                    <span className="text-content-muted shrink-0">Personnes souhaitées</span>
                                    <span className="font-bold text-content text-right">{it.desired_people.join(', ')}</span>
                                </div>
                            )}
                            {(it.start_time || it.end_time) && (
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-content-muted">Horaires</span>
                                    <span className="font-bold text-content">{it.start_time}{it.end_time ? ` - ${it.end_time}` : ''}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {error && <p className="text-xs text-red-500 font-semibold text-center">{error}</p>}

                {confirmedAt ? (
                    <div className="flex flex-col items-center gap-2 text-center py-2">
                        <CheckCircle2 size={32} className="text-green-500" />
                        <p className="text-sm font-bold text-content">Demande confirmée</p>
                        <p className="text-xs text-content-muted">Merci, {info.company_name} a été prévenu que vous avez pris en compte cette demande.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5">Commentaire</label>
                            <textarea value={namesText} onChange={(e) => setNamesText(e.target.value)} rows={Math.min(6, Math.max(2, info.items.reduce((sum, it) => sum + (it.quantity || 0), 0)))}
                                className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5">Document à joindre (facultatif)</label>
                            {file ? (
                                <div className="flex items-center justify-between gap-2 bg-surface-soft border border-border rounded-lg px-3 py-2">
                                    <span className="text-xs text-content truncate flex items-center gap-1.5"><Paperclip size={12} /> {file.name}</span>
                                    <button onClick={() => setFile(null)} className="text-content-muted hover:text-content shrink-0"><X size={14} /></button>
                                </div>
                            ) : (
                                <label className="flex items-center justify-center gap-2 border border-dashed border-border rounded-lg px-3 py-3 text-xs text-content-muted cursor-pointer hover:border-brand-500/40 transition-colors">
                                    <Paperclip size={13} /> Ajouter un fichier (bon de commande, planning...)
                                    <input type="file" accept="image/png,image/jpeg,image/gif,application/pdf" onChange={handleFileChange} className="hidden" />
                                </label>
                            )}
                        </div>
                        <button onClick={handleConfirm} disabled={confirming}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm disabled:opacity-50">
                            {confirming ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                            {confirming ? 'Confirmation...' : 'Confirmer la demande'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
